<template>
  <div class="notifications-container">
    <transition-group name="fade">
      <div 
        v-for="notification in notifications" 
        :key="notification.id" 
        :class="['notification', `notification-${notification.type}`]"
        role="alert"
      >
        <div class="notification-icon">
          <font-awesome-icon :icon="iconForType(notification.type)" />
        </div>
        <div class="notification-content">
          <div v-if="notification.title" class="notification-title">{{ notification.title }}</div>
          <div class="notification-message">{{ notification.message }}</div>
          <div v-if="notification.action" class="notification-action">
            <button 
              @click="handleAction(notification.action, notification.id)"
              class="notification-action-btn"
            >
              {{ notification.action.text }}
            </button>
          </div>
        </div>
        <button 
          type="button" 
          class="notification-close"
          @click="removeNotification(notification.id)"
          aria-label="Close"
        >
          <font-awesome-icon icon="times" />
        </button>
        <div 
          v-if="notification.duration > 0" 
          class="notification-progress"
          :style="{ animationDuration: `${notification.duration}ms` }"
        ></div>
      </div>
    </transition-group>
  </div>
</template>

<script>
import { ref, computed, onUnmounted, watch } from 'vue';
import { useStore } from 'vuex';

export default {
  name: 'Notifications',
  
  setup() {
    const store = useStore();
    
    // Get notifications from Vuex store
    const notifications = computed(() => {
      return store.state.notifications.items;
    });
    
    // Initialize timer references
    const timers = ref({});
    
    
    // Keep track of processed notification IDs to detect new ones
    const processedIds = ref(new Set());
    
    // Watch for new notifications being added
    watch(notifications, (newNotifications) => {
      // Find newly added notifications by checking against processed IDs
      const newNotifications_filtered = newNotifications.filter(notification => {
        return !processedIds.value.has(notification.id);
      });
      
      // Set up dismiss timers for new notifications and mark them as processed
      newNotifications_filtered.forEach(notification => {
        processedIds.value.add(notification.id);
        if (notification.duration > 0) {
          setDismissTimer(notification);
        }
      });
      
      // Clean up processed IDs for notifications that no longer exist
      const currentIds = new Set(newNotifications.map(n => n.id));
      processedIds.value.forEach(id => {
        if (!currentIds.has(id)) {
          processedIds.value.delete(id);
        }
      });
    }, { immediate: true });
    
    // Subscribe to store changes directly for better reactivity
    const unsubscribe = store.subscribe((mutation) => {
      if (mutation.type === 'notifications/ADD_NOTIFICATION') {
        const notification = mutation.payload;
        if (!processedIds.value.has(notification.id)) {
          processedIds.value.add(notification.id);
          if (notification.duration > 0) {
            setDismissTimer(notification);
          }
        }
      }
    });
    
    // Clean up timers and subscription on unmount
    onUnmounted(() => {
      Object.values(timers.value).forEach(timer => {
        clearTimeout(timer);
      });
      unsubscribe();
    });
    
    // Set timer to dismiss notification
    const setDismissTimer = (notification) => {
      // Clear existing timer if it exists
      if (timers.value[notification.id]) {
        clearTimeout(timers.value[notification.id]);
      }
      
      // Set new timer
      timers.value[notification.id] = setTimeout(() => {
        removeNotification(notification.id);
        delete timers.value[notification.id];
      }, notification.duration);
    };
    
    // Remove notification
    const removeNotification = (id) => {
      // Clear timer if it exists
      if (timers.value[id]) {
        clearTimeout(timers.value[id]);
        delete timers.value[id];
      }
      
      // Remove from processed IDs
      processedIds.value.delete(id);
      
      // Remove notification from store
      store.dispatch('notifications/remove', id);
    };
    
    // Determine icon based on notification type
    const iconForType = (type) => {
      switch (type) {
        case 'success':
          return 'check-circle';
        case 'error':
          return 'exclamation-circle';
        case 'warning':
          return 'exclamation-triangle';
        case 'info':
          return 'info-circle';
        default:
          return 'bell';
      }
    };
    
    // Handle action button click
    const handleAction = (action, notificationId) => {
      if (action && typeof action.handler === 'function') {
        action.handler();
        // Optionally remove notification after action
        if (action.removeAfterAction !== false) {
          removeNotification(notificationId);
        }
      }
    };
    
    return {
      notifications,
      removeNotification,
      iconForType,
      handleAction
    };
  }
};
</script>

<style scoped>
.notifications-container {
  position: fixed;
  top: 24px;
  right: 24px;
  z-index: 9999;
  max-width: 400px;
  width: 100%;
  pointer-events: none;
}

.notification {
  display: flex;
  align-items: flex-start;
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
  box-shadow: 
    0 8px 32px rgba(0, 0, 0, 0.12),
    0 2px 8px rgba(0, 0, 0, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  margin-bottom: 16px;
  padding: 20px;
  position: relative;
  overflow: hidden;
  min-height: 72px;
  pointer-events: auto;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.notification:hover {
  transform: translateY(-2px);
  box-shadow: 
    0 12px 40px rgba(0, 0, 0, 0.15),
    0 4px 12px rgba(0, 0, 0, 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.6);
}

.notification-icon {
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  margin-right: 16px;
  margin-top: 2px;
  font-weight: 600;
}

.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-weight: 600;
  font-size: 15px;
  margin-bottom: 6px;
  color: #1a1a1a;
  line-height: 1.3;
}

.notification-message {
  font-size: 14px;
  line-height: 1.5;
  word-break: break-word;
  color: #4a4a4a;
  font-weight: 400;
}

.notification-action {
  margin-top: 12px;
}

.notification-action-btn {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border: none;
  border-radius: 8px;
  color: white;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  padding: 8px 16px;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 2px 8px rgba(102, 126, 234, 0.3);
}

.notification-action-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.notification-success .notification-action-btn {
  background: linear-gradient(135deg, #4ade80 0%, #22c55e 100%);
  box-shadow: 0 2px 8px rgba(74, 222, 128, 0.3);
}

.notification-success .notification-action-btn:hover {
  box-shadow: 0 4px 12px rgba(74, 222, 128, 0.4);
}

.notification-error .notification-action-btn {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
  box-shadow: 0 2px 8px rgba(248, 113, 113, 0.3);
}

.notification-error .notification-action-btn:hover {
  box-shadow: 0 4px 12px rgba(248, 113, 113, 0.4);
}

.notification-warning .notification-action-btn {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
}

.notification-warning .notification-action-btn:hover {
  box-shadow: 0 4px 12px rgba(251, 191, 36, 0.4);
}

.notification-close {
  background: transparent;
  border: none;
  color: #9ca3af;
  cursor: pointer;
  font-size: 18px;
  padding: 6px;
  margin-left: 12px;
  opacity: 0.6;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 6px;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.notification-close:hover {
  opacity: 1;
  background: rgba(0, 0, 0, 0.05);
  color: #6b7280;
}

.notification-progress {
  position: absolute;
  bottom: 0;
  left: 0;
  height: 4px;
  width: 100%;
  transform-origin: left center;
  animation-name: progress;
  animation-timing-function: linear;
  animation-fill-mode: forwards;
  border-radius: 0 0 16px 16px;
}

/* Notification types */
.notification-success {
  border-left: 4px solid #22c55e;
}

.notification-success .notification-icon {
  background: linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%);
  color: #16a34a;
}

.notification-success .notification-progress {
  background: linear-gradient(90deg, #22c55e 0%, #16a34a 100%);
}

.notification-error {
  border-left: 4px solid #ef4444;
}

.notification-error .notification-icon {
  background: linear-gradient(135deg, #fef2f2 0%, #fecaca 100%);
  color: #dc2626;
}

.notification-error .notification-progress {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
}

.notification-warning {
  border-left: 4px solid #f59e0b;
}

.notification-warning .notification-icon {
  background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
  color: #d97706;
}

.notification-warning .notification-progress {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
}

.notification-info {
  border-left: 4px solid #3b82f6;
}

.notification-info .notification-icon {
  background: linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%);
  color: #2563eb;
}

.notification-info .notification-progress {
  background: linear-gradient(90deg, #3b82f6 0%, #2563eb 100%);
}

/* Animations */
.fade-enter-active,
.fade-leave-active {
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.fade-enter-from {
  opacity: 0;
  transform: translateX(40px) scale(0.95);
}

.fade-leave-to {
  opacity: 0;
  transform: translateX(40px) scale(0.95);
}

@keyframes progress {
  0% {
    transform: scaleX(1);
  }
  100% {
    transform: scaleX(0);
  }
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .notification {
    background: rgba(17, 24, 39, 0.95);
    border: 1px solid rgba(75, 85, 99, 0.3);
    box-shadow: 
      0 8px 32px rgba(0, 0, 0, 0.3),
      0 2px 8px rgba(0, 0, 0, 0.2),
      inset 0 1px 0 rgba(255, 255, 255, 0.1);
  }
  
  .notification:hover {
    box-shadow: 
      0 12px 40px rgba(0, 0, 0, 0.4),
      0 4px 12px rgba(0, 0, 0, 0.3),
      inset 0 1px 0 rgba(255, 255, 255, 0.15);
  }
  
  .notification-title {
    color: #f9fafb;
  }
  
  .notification-message {
    color: #d1d5db;
  }
  
  .notification-close {
    color: #9ca3af;
  }
  
  .notification-close:hover {
    background: rgba(255, 255, 255, 0.1);
    color: #e5e7eb;
  }
}

/* Responsive adjustments */
@media screen and (max-width: 480px) {
  .notifications-container {
    top: 16px;
    right: 16px;
    left: 16px;
    max-width: none;
    width: auto;
  }
  
  .notification {
    padding: 16px;
    border-radius: 12px;
  }
  
  .notification-icon {
    width: 20px;
    height: 20px;
    font-size: 12px;
    margin-right: 12px;
  }
  
  .notification-title {
    font-size: 14px;
  }
  
  .notification-message {
    font-size: 13px;
  }
}
</style>